import { Component, Input, OnChanges } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  ActionFailed,
  Rental,
  RentalSecurityDepositConfig,
  Toaster,
  updateRentalSecurityDepositConfig,
} from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { Store } from '@ngrx/store'
import { finalize } from 'rxjs/operators'
import { FeeModalities, FeeTypes } from '@tokeet/cost-resolver'
import {
  RentalSecurityDepositUpdatePayload,
  RentalSecurityDepositService,
} from '../../../store/security-deposit/rental-security-deposit.service'

interface RentalSecurityDepositFormValue {
  amount: number | null
  type: FeeTypes
  modality: FeeModalities
  status: boolean
}

@Component({
  selector: 'app-rental-security-deposit',
  templateUrl: './rental-security-deposit.component.html',
  styleUrls: ['./rental-security-deposit.component.scss'],
})
export class RentalSecurityDepositComponent implements OnChanges {
  @Input() rental: Rental

  isLoading = false
  processing = false

  depositForm: FormGroup

  defaultDeposit: RentalSecurityDepositFormValue = {
    amount: null,
    type: FeeTypes.Flat,
    modality: FeeModalities.PerStay,
    status: true,
  }

  existingDeposit: RentalSecurityDepositConfig | null = null

  currencyCode: string

  constructor(
    private fb: FormBuilder,
    private store: Store<any>,
    private securityDepositService: RentalSecurityDepositService,
    private toaster: Toaster
  ) {}

  ngOnChanges() {
    this.refresh()
    this.currencyCode = R.pathOr('USD', ['currency', 'code'], this.rental)
  }

  refresh() {
    this.isLoading = true
    this.securityDepositService
      .loadConfig(this.rental)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (config) => {
          this.existingDeposit = config
          this.initForm(this.existingDeposit)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  defineForm() {
    return this.fb.group({
      amount: [null, [Validators.required, Validators.min(1), Validators.max(100)]],
      modality: [FeeModalities.PerStay, [Validators.required]],
      type: [FeeTypes.Flat, [Validators.required]],
      status: [true],
    })
  }

  initForm(deposit: RentalSecurityDepositConfig | null) {
    if (!this.depositForm) {
      this.depositForm = this.defineForm()
    }

    const value: RentalSecurityDepositFormValue = deposit
      ? {
          amount: deposit.amount,
          type: deposit.type,
          modality: deposit.modality,
          status: deposit.status,
        }
      : { ...this.defaultDeposit }

    this.depositForm.reset(value)
  }

  save() {
    if (this.depositForm.invalid) {
      this.depositForm.markAllAsTouched()
      return
    }

    this.processing = true

    const data = this.depositForm.getRawValue() as RentalSecurityDepositFormValue
    const payload: RentalSecurityDepositUpdatePayload = {
      amount: Number(data.amount),
      type: data.type,
      modality: data.modality,
      status: data.status,
    }

    const request$ = this.securityDepositService.saveConfig(this.rental.id, payload)

    request$.pipe(finalize(() => (this.processing = false))).subscribe(
      (updatedConfig) => {
        this.existingDeposit = updatedConfig
        this.store.dispatch(updateRentalSecurityDepositConfig({ rentalId: this.rental.id, config: updatedConfig }))
        this.initForm(updatedConfig)
        this.toaster.success($localize`Security deposit saved successfully`)
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }
}
